import {CustomFieldResourceTypeEnum} from '@frontapp/workflows/custom_fields';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {useEventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {isDefined} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {getHasSearchMultipleCustomFieldQueryBuilder} from '../../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {wrapInShallowSelector} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import type {CustomFieldModel} from '../../../../../../models/api/customFieldModel';
import {getFlatCustomFields} from '../../../../../../store/data/dataSelectors';
import {ConversationCustomFieldNameField} from './conversationCustomFieldNameField';
import {CustomAttributeValueEditor} from './customAttributeValueEditor';

/*
 * Props.
 */

interface CustomField {
  name: string;
  value: string | undefined;
}

export interface ConversationCustomFieldFieldProps {
  selectedCustomFieldName: string | undefined;
  selectedCustomFields: ReadonlyArray<CustomField>;
  selectedCustomFieldValue: string | undefined;
  onChange: (arg: {name: string; value: string | undefined} | undefined) => void;
  onChangeMultiple: (arg: Array<CustomField>) => void;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: left;
  gap: 10px;
  width: 100%;
`;

const StyledCustomFieldEditorDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 4px;
  width: 100%;
`;

const StyledFlexShinkDiv = styled.div`
  flex-shrink: 0;
  max-width: 158px;
`;

/*
 * Components.
 */

export const ConversationCustomFieldField: FC<ConversationCustomFieldFieldProps> = (props) => {
  const {
    selectedCustomFieldName,
    selectedCustomFieldValue,
    selectedCustomFields,
    onChange,
    onChangeMultiple,
  } = props;

  const customFields = useAppSelector(wrapInShallowSelector(getFlatCustomFields));
  const conversationCustomFields: Array<CustomFieldModel> = useMemo(
    () =>
      customFields.filter(
        (customField) => customField.resourceType === CustomFieldResourceTypeEnum.CONVERSATION,
      ),
    [customFields],
  );
  const hasMultipleCustomFieldQueryBuilder = useAppSelector(getHasSearchMultipleCustomFieldQueryBuilder);

  const selectedCustomField = useMemo(() => {
    if (!selectedCustomFieldName) {
      return undefined;
    }
    // We need to find the id of the custom field.
    return conversationCustomFields.find((cf) => cf.name === selectedCustomFieldName);
    // FIXME(ldemarne): What if we can't find the selected custom field?
  }, [conversationCustomFields, selectedCustomFieldName]);

  const nonSelectedCustomFields = useMemo(
    () => conversationCustomFields.filter((cf) => !selectedCustomFields.some((scf) => scf.name === cf.name)),
    [conversationCustomFields, selectedCustomFields],
  );

  const handleNameChange = useEventHandler((oldName: string, newName?: string) => {
    const newSelectedCustomFields = [...selectedCustomFields];
    const changedCustomField = newSelectedCustomFields.find((cf) => cf.name === oldName);
    if (!changedCustomField) {
      return;
    }
    if (!newName) {
      onChangeMultiple(newSelectedCustomFields.filter((cf) => cf.name !== oldName));
      return;
    }
    changedCustomField.name = newName;
    changedCustomField.value = undefined;
    onChangeMultiple(newSelectedCustomFields);
  });

  const handleValueChange = useEventHandler((newCustomField) => {
    const newSelectedCustomFields = [...selectedCustomFields];
    const changedCustomField = newSelectedCustomFields.find((cf) => cf.name === newCustomField.name);
    if (!changedCustomField) {
      return;
    }
    changedCustomField.value = newCustomField.value;
    onChangeMultiple(newSelectedCustomFields);
  });

  const handleNewCustomField = useEventHandler((name?) => {
    if (!name || selectedCustomFields.some((cf) => cf.name === name)) {
      return;
    }
    onChangeMultiple([...selectedCustomFields, {name, value: undefined}]);
  });

  const handleChangeSingleValue = useEventHandler((newCustomField) => {
    onChange(
      newCustomField ? {name: newCustomField.name, value: newCustomField.value ?? undefined} : undefined,
    );
  });

  if (hasMultipleCustomFieldQueryBuilder) {
    return (
      <StyledWrapper>
        {selectedCustomFields?.length > 0
          ? selectedCustomFields
              .map((scf) => {
                const selectedCF = conversationCustomFields.find(
                  (conversationCustomField) => conversationCustomField.name === scf.name,
                );
                if (!selectedCF) {
                  return undefined;
                }
                return (
                  <CustomFieldEditor
                    key={scf.name}
                    customField={scf}
                    selectedCustomField={selectedCF}
                    handleNameChange={handleNameChange}
                    handleValueChange={handleValueChange}
                    conversationCustomFields={nonSelectedCustomFields}
                  />
                );
              })
              .filter((cf) => isDefined(cf))
          : null}

        {/** This is the empty Custom field name selector to add new fields */}
        <ConversationCustomFieldNameField
          selectedCustomField={undefined}
          conversationCustomFields={nonSelectedCustomFields}
          onChange={(newCustomField) => handleNewCustomField(newCustomField?.name)}
        />
      </StyledWrapper>
    );
  }

  return (
    <StyledButtonContentDiv>
      <ConversationCustomFieldNameField
        selectedCustomField={selectedCustomField}
        conversationCustomFields={conversationCustomFields}
        onChange={handleChangeSingleValue}
      />
      {selectedCustomField && (
        <CustomAttributeValueEditor
          customAttribute={{customField: selectedCustomField, value: selectedCustomFieldValue ?? ''}}
          editedValue={selectedCustomFieldValue ?? ''}
          onEdit={(newValue) => {
            handleChangeSingleValue({name: selectedCustomField.name, value: newValue || undefined});
          }}
          onEditSave={(newValue) => {
            handleChangeSingleValue({name: selectedCustomField.name, value: newValue || undefined});
          }}
          onEditCancel={() => {}}
        />
      )}
    </StyledButtonContentDiv>
  );
};

interface CustomFieldEditorProps {
  customField: CustomField;
  handleNameChange: (oldName: string, newName?: string) => void;
  handleValueChange: (newCustomField: CustomField) => void;
  selectedCustomField: CustomFieldModel;
  conversationCustomFields: Array<CustomFieldModel>;
}

const CustomFieldEditor: FC<CustomFieldEditorProps> = ({
  customField,
  selectedCustomField,
  handleNameChange,
  handleValueChange,
  conversationCustomFields,
}) => (
  <StyledCustomFieldEditorDiv>
    <StyledFlexShinkDiv>
      <ConversationCustomFieldNameField
        selectedCustomField={selectedCustomField}
        conversationCustomFields={conversationCustomFields}
        onChange={(newCustomField) => handleNameChange(customField.name, newCustomField?.name)}
      />
    </StyledFlexShinkDiv>
    <CustomAttributeValueEditor
      customAttribute={{customField: selectedCustomField, value: customField.value ?? ''}}
      editedValue={customField.value ?? ''}
      onEdit={(newValue) => {
        handleValueChange({name: selectedCustomField.name, value: newValue || undefined});
      }}
      onEditSave={(newValue) => {
        handleValueChange({name: selectedCustomField.name, value: newValue || undefined});
      }}
      onEditCancel={() => {}}
    />
  </StyledCustomFieldEditorDiv>
);
